// components/GLSMap.tsx
import React, { useEffect, useRef, useState } from "react";
import { convertToGlsPoint } from "../../../types/deliveryPoints.type";

// Typ dla oryginalnej odpowiedzi API GLS (snake_case)

export const GLSMap = ({
  onPointSelected,
  isVisible,
  onClose
  // postalCode,
}) => {
  const mapRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const parcelInstanceRef = useRef(null);
  const intervalRef = useRef(null);

  // Helper function to convert snake_case response to camelCase
  const handlePointSelection = apiPoint => {
    try {
      const convertedPoint = convertToGlsPoint(apiPoint);
      console.log("Converted GLS point:", convertedPoint);
      onPointSelected(convertedPoint);
      setIsLoading(false);
    } catch (error) {
      console.error("Error converting GLS point:", error);
      setIsLoading(false);
    }
  };

  // Załaduj skrypt GLS
  useEffect(() => {
    const loadGLSScript = () => {
      if (window.SzybkaPaczkaMap && window.SzybkaPaczkaParcel) {
        setIsScriptLoaded(true);
        return;
      }
      const existingScript = document.querySelector('script[src*="mapa.gls-poland.com"]');
      if (existingScript) {
        // Script already exists, check if objects are available
        const checkGLSObjects = () => {
          if (window.SzybkaPaczkaMap && window.SzybkaPaczkaParcel) {
            setIsScriptLoaded(true);
          } else {
            setTimeout(checkGLSObjects, 100);
          }
        };
        checkGLSObjects();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://mapa.gls-poland.com/js/v4.0/maps_sdk.js";
      script.async = true;
      script.onload = () => {
        console.log("GLS script loaded");
        // Poczekaj chwilę na pełne załadowanie wszystkich funkcji
        const checkGLSObjects_0 = () => {
          if (window.SzybkaPaczkaMap && window.SzybkaPaczkaParcel) {
            setIsScriptLoaded(true);
            setScriptError(false);
          } else {
            setTimeout(checkGLSObjects_0, 100);
          }
        };
        checkGLSObjects_0();
      };
      script.onerror = () => {
        console.error("Nie udało się załadować skryptu GLS");
        setScriptError(true);
        setIsLoading(false);
      };
      document.head.appendChild(script);
    };
    if (!scriptError) {
      loadGLSScript();
    }
  }, [scriptError]);

  // Inicjalizuj mapę gdy jest widoczna
  useEffect(() => {
    if (isVisible && isScriptLoaded && mapRef.current && !scriptError) {
      setIsLoading(true);
      try {
        // Wyczyść poprzedni kontener mapy
        const mapContainer = mapRef.current;
        mapContainer.innerHTML = "";

        // Inicjalizacja mapy GLS zgodnie z dokumentacją
        window.SzybkaPaczkaMap.init({
          lang: "PL",
          country_parcelshops: "PL",
          el: "gls-map-container",
          parcel_weight: "5",
          geolocation: true,
          map_type: false // Używamy domyślnych map OpenStreetMap
        });

        // Utworzenie instancji do pobierania danych
        parcelInstanceRef.current = new window.SzybkaPaczkaParcel();

        // Nasłuchuj na event wyboru punktu (sposób pierwszy z dokumentacji)
        const handleParcelShopSelection = e => {
          if (e.detail && e.detail.ParcelShop) {
            handlePointSelection(e.detail.ParcelShop);
            // Clear interval when point is selected via event
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        };
        window.addEventListener("get_parcel_shop", handleParcelShopSelection);

        // Alternatywnie - sprawdzaj wybór punktu co 500ms (sposób drugi z dokumentacji)
        intervalRef.current = setInterval(() => {
          if (parcelInstanceRef.current) {
            try {
              const parcelData = parcelInstanceRef.current.getParcelObject();
              if (parcelData && parcelData.selected) {
                handlePointSelection(parcelData.selected);
                // Wyczyść interval po wyborze punktu
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }
              }
            } catch (error_1) {
              // Ignore errors when checking for selected point
              console.log("GLS polling error (expected):", error_1.message);
            }
          }
        }, 500);
        setIsLoading(false);

        // Cleanup function
        return () => {
          window.removeEventListener("get_parcel_shop", handleParcelShopSelection);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };
      } catch (error_0) {
        console.error("Błąd inicjalizacji mapy GLS:", error_0);
        setScriptError(true);
        setIsLoading(false);
      }
    }

    // Cleanup when component is hidden
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, isScriptLoaded, scriptError]);
  if (!isVisible) return null;

  // Jeśli wystąpił błąd ładowania skryptów
  if (scriptError) {
    return <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 shadow-xl max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Błąd ładowania mapy GLS
            </h3>
            <p className="text-gray-600 mb-4">
              Nie udało się załadować mapy punktów GLS.
            </p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => {
              setScriptError(false);
              setIsScriptLoaded(false);
            }} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                Spróbuj ponownie
              </button>
              <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-[95%] h-[95%] max-w-[1400px] max-h-[800px] bg-white rounded-lg shadow-xl overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            Wybierz punkt odbioru GLS
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" aria-label="Zamknij mapę">
            ×
          </button>
        </div>

        {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Ładowanie mapy GLS...</p>
            </div>
          </div>}

        <div className="w-full h-[calc(100%-64px)] relative">
          <div id="gls-map-container" ref={mapRef} className="w-full h-full" style={{
          minHeight: "500px"
        }} />
        </div>
      </div>
    </div>;
};